'use es6';

import { memo, useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import devLogger from 'react-utils/devLogger';
import { makeUniversalSidebarCardDnDWrapper } from './UniversalSidebarCardDnDWrapper';
import { CardLocationContext } from './context/CardLocationContext';
import CustomDragLayer from 'customer-data-ui-utilities/dnd/CustomDragLayer';
import { List } from 'foundations-components/transitional/components/List';
import UniversalSidebarCardDragPreview from './UniversalSidebarCardDragPreview';
import { CardStateManipulationContext } from './context/CardStateManipulationContext';
import { SourceAppContext } from './context/SourceAppContext';
import Raven from 'raven-js';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const UniversalSidebarCards = ({
  cards,
  cardProps,
  draggable,
  objectId,
  objectType,
  cardComponents
}) => {
  const {
    cardLocation
  } = useContext(CardLocationContext);
  const {
    sourceApp
  } = useContext(SourceAppContext);
  const {
    onCardOrderChange
  } = useContext(CardStateManipulationContext);
  const [draggingFromIndex, setDraggingFromIndex] = useState(null);
  const [draggingToIndex, setDraggingToIndex] = useState(null);
  const [previewWidth, setPreviewWidth] = useState(null);
  const handleMoveCard = useCallback((from, to) => {
    if (draggingFromIndex === null) {
      setDraggingFromIndex(from);
    }
    setDraggingToIndex(to);
  }, [draggingFromIndex, setDraggingFromIndex, setDraggingToIndex]);
  const handleDropCard = useCallback(() => {
    if (draggingToIndex === null) return;
    const newOrderedCards = [...cards];
    const [movedCard] = newOrderedCards.splice(draggingFromIndex, 1);
    newOrderedCards.splice(draggingToIndex, 0, movedCard);
    setDraggingFromIndex(null);
    setDraggingToIndex(null);
    onCardOrderChange(newOrderedCards);
    CrmLogger.log('sidebar-interaction', {
      action: 'reorder cards',
      cardId: movedCard.cardId,
      startIndex: draggingFromIndex,
      endIndex: draggingToIndex
    });
  }, [cards, draggingFromIndex, draggingToIndex, setDraggingFromIndex, setDraggingToIndex, onCardOrderChange]);
  const calculatePreviewWidth = useCallback(ref => {
    if (ref) {
      setPreviewWidth(ref.clientWidth);
    }
  }, [setPreviewWidth]);
  return /*#__PURE__*/_jsx("div", {
    ref: calculatePreviewWidth,
    children: /*#__PURE__*/_jsxs(List, {
      "data-test-id": "sidebar-cards-list",
      wrapChildren: true,
      children: [cards.map((cardData, index) => {
        const cardConfig = cardComponents[cardData.__typename];
        if (!cardConfig) {
          const cardType = cardData.__typename;
          devLogger.warn({
            message: `[UniversalSidebarCards] no card found for ${cardType} (${cardLocation})`,
            key: `UniversalSidebarCards:${cardType}`
          });
          Raven.captureMessage(`UniversalSidebar: no config found for card`, {
            extra: {
              cardType,
              cardLocation,
              sourceApp
            }
          });
          return null;
        }
        const singleCardProps = cardProps ? cardProps[cardData.__typename] : null;
        const UniversalSidebarCardDnDWrapper = makeUniversalSidebarCardDnDWrapper(cardLocation);
        return /*#__PURE__*/_jsx(UniversalSidebarCardDnDWrapper, {
          Actions: cardConfig.Actions,
          cardProps: singleCardProps,
          Content: cardConfig.Content,
          data: cardData,
          draggable: draggable,
          isBordered: index > 0,
          objectType: objectType,
          objectId: objectId,
          Title: cardConfig.Title
          /* drag and drop props */,
          index: index,
          draggingFromIndex: draggingFromIndex,
          draggingToIndex: draggingToIndex,
          dragId: cardData.cardId,
          onMove: handleMoveCard,
          onDrop: handleDropCard,
          useCustomDragLayer: true
        }, cardData.cardId);
      }), /*#__PURE__*/_jsx(CustomDragLayer, {
        namespace: cardLocation,
        children: /*#__PURE__*/_jsx(UniversalSidebarCardDragPreview, {
          width: previewWidth
        })
      })]
    })
  });
};
UniversalSidebarCards.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.shape({
    __typename: PropTypes.string.isRequired,
    cardId: PropTypes.string.isRequired,
    isCollapsed: PropTypes.bool.isRequired,
    label: PropTypes.string
  })).isRequired,
  cardProps: PropTypes.object,
  objectType: PropTypes.string.isRequired,
  cardComponents: PropTypes.object,
  objectId: PropTypes.string.isRequired,
  draggable: PropTypes.bool
};
export default /*#__PURE__*/memo(UniversalSidebarCards);